/**
 * @fileoverview Inline modal for quick group creation within the review drawer.
 * onSave returns a boolean: true if saved successfully, false if it failed.
 * The modal only closes and resets on success.
 */

import { useState } from 'react'
import { Modal, TextInput, Group, Button } from '@mantine/core'
import { IconDeviceFloppy } from '@tabler/icons-react'
import { useArcTranslation } from 'hooks'

interface Props {
  opened: boolean
  onClose: () => void
  onSave: (name: string) => Promise<boolean>
  loading: boolean
}

export function AddGroupModal({ opened, onClose, onSave, loading }: Props) {
  const { t } = useArcTranslation()
  const [name, setName] = useState('')
  const [error, setError] = useState('')

  const handleSave = async () => {
    const trimmed = name.trim()
    if (!trimmed) {
      setError(t('student_requests.required_field'))
      return
    }

    const success = await onSave(trimmed)

    // Only close and reset if the save actually succeeded.
    // If it failed, keep the modal open with the user's input so they can retry.
    if (success) {
      setName('')
      setError('')
      onClose()
    }
  }

  const handleClose = () => {
    // Only allow closing manually when not mid-save
    if (loading) return
    setName('')
    setError('')
    onClose()
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={t('student_requests.add_group')}
      size='sm'
      centered
      zIndex={200}
      closeOnClickOutside={!loading}
      closeOnEscape={!loading}
    >
      <TextInput
        label={t('student_requests.group_name')}
        placeholder={t('student_requests.group_name')}
        value={name}
        onChange={e => {
          setName(e.currentTarget.value)
          if (error) setError('')
        }}
        error={error}
        mb='md'
        disabled={loading}
      />
      <Group position='right'>
        <Button variant='default' onClick={handleClose} disabled={loading}>
          {t('student_requests.cancel')}
        </Button>
        <Button
          color='teal'
          leftIcon={<IconDeviceFloppy size={16} />}
          onClick={handleSave}
          loading={loading}
        >
          {t('student_requests.save')}
        </Button>
      </Group>
    </Modal>
  )
}
